import styles from "./MarketComponentDetailsDialog.desktop.module.css";
import DialogView from "../../dialog/Dialog.view";
import { useTranslation } from "react-i18next";
import AreYouSureDialogView from "../../are-you-sure-dialog/AreYouSureDialog.view";
import HistoryDialogView from "../../history-dialog/HistoryDialog.view";
import { useMarketComponentDetailsDialogController } from "../MarketComponentDetailsDialog.controller";
import canViewMarketComponentHistory from "../../../security/CanViewMarketComponentHistory";
import { convertColorToStatus } from "../../../tools/converters/StatusColorConverter";
import DatePickerView from "../../../components/date-picker/DatePicker.view";
import dayjs from "dayjs";
import { MarketComponentMarketComponentIOMarketComponentStatusEnum } from "../../../generated/fresh-guard-api";
import type MarketComponentDetailsDialogProperties from "../MarketComponentDetailsDialog.properties.ts";
import React, { type CSSProperties } from "react";
import LoaderView from "../../../components/loader/Loader.view.tsx";
import { parseStringToDate } from "../../../tools/converters/DateConverter.ts";

export default function MarketComponentDetailsDialogDesktopView(
  props: MarketComponentDetailsDialogProperties,
) {
  const { t } = useTranslation();
  const {
    isAreYouSureDialogOpen,
    isHistoryDialogOpen,
    switchToRedAfterDate,
    handleChangeShelfLifeClick,
    handleYesClick,
    handleNoClick,
    handleDialogGoToHistoryClick,
    handleHistoryDialogCloseClick,
    editSwitchToRedAfterDate,
    loading,
    disabledYes,
  } = useMarketComponentDetailsDialogController(props);

  return (
    <>
      {isAreYouSureDialogOpen && (
        <AreYouSureDialogView
          message={t("ui.changeShelfLifeMessage")}
          handleNo={handleNoClick}
          handleYes={handleYesClick}
          disabledYes={disabledYes}
        >
          <table className={styles.table}>
            <tbody>
              <tr>
                <td className={styles.tdKey}>{t("ui.switchToRedAfterDate")}</td>
                <td className={styles.tdValue}>
                  <DatePickerView
                    value={
                      switchToRedAfterDate
                        ? new Date(switchToRedAfterDate)
                        : null
                    }
                    placeholder={t("ui.switchToRedAfterDatePlaceholder")}
                    dateFormat={t("date.short")}
                    onChange={editSwitchToRedAfterDate}
                    switchToRedAfterXDays={
                      props.marketComponent?.switchToRedAfterXDays
                    }
                    daysBeforeSwitchToRed={
                      props.marketComponent?.daysBeforeSwitchToRed
                    }
                    open
                    showNextThreeMonthButtons
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </AreYouSureDialogView>
      )}
      {isHistoryDialogOpen && (
        <HistoryDialogView
          id={props.marketComponent?.id ?? 0}
          isMarketId={false}
          showMarketComponentName={false}
          name={t("ui.historyDialogMarketComponentHeading")}
          handleDialogCloseClick={handleHistoryDialogCloseClick}
        />
      )}

      {loading && <LoaderView />}

      {!loading && props.marketComponent && (
        <DialogView
          header={props.marketComponent.shortDescription ?? ""}
          onBackdrop={props.handleDialogCloseClick}
        >
          <>
            <table className={styles.table}>
              <tbody>
                <tr>
                  <td className={styles.tdKey}>{t("ui.categoryLabel")}</td>
                  <td
                    className={styles.tdValue}
                    style={
                      {
                        "--color": "#252525",
                      } as CSSProperties
                    }
                  >
                    {props.marketComponent.articleType}
                  </td>
                </tr>
                <tr>
                  <td className={styles.tdKey}>{t("ui.statusLabel")}</td>
                  <td
                    className={styles.tdValue}
                    style={
                      {
                        "--color":
                          props.marketComponent.marketComponentStatus ===
                          MarketComponentMarketComponentIOMarketComponentStatusEnum.Ffffff
                            ? "#000000"
                            : props.marketComponent.marketComponentStatus,
                      } as CSSProperties
                    }
                  >
                    {convertColorToStatus(
                      props.marketComponent.marketComponentStatus,
                      t,
                    )}
                  </td>
                </tr>
                <tr>
                  <td className={styles.tdKey}>{t("ui.number")}</td>
                  <td
                    className={styles.tdValue}
                    style={
                      {
                        "--color": "#252525",
                      } as CSSProperties
                    }
                  >
                    {props.marketComponent.number}
                  </td>
                </tr>
                <tr>
                  <td className={styles.tdKey}>{t("ui.criticalDate")}</td>
                  <td
                    className={styles.tdValue}
                    style={
                      {
                        "--color": "#252525",
                      } as CSSProperties
                    }
                  >
                    {props.marketComponent.switchToRedAfterDate
                      ? dayjs(
                          props.marketComponent.switchToRedAfterDate,
                        ).format(t("date.shortDayJS"))
                      : ""}
                  </td>
                </tr>
                <tr>
                  <td className={styles.tdKey}>{t("ui.lastValidationDate")}</td>
                  <td
                    className={styles.tdValue}
                    style={
                      {
                        "--color": "#252525",
                      } as CSSProperties
                    }
                  >
                    {props.marketComponent.lastValidation
                      ? parseStringToDate(
                          props.marketComponent.lastValidation,
                          t,
                        )
                      : ""}
                  </td>
                </tr>
              </tbody>
            </table>
            <div className={styles.buttonContainer}>
              <button
                className={styles.buttonChangeShelfLife}
                onClick={handleChangeShelfLifeClick}
              >
                {t("ui.changeShelfLife")}
              </button>
              {canViewMarketComponentHistory() && (
                <>
                  <button
                    className={styles.buttonHistory}
                    onClick={handleDialogGoToHistoryClick}
                  >
                    {t("ui.goToHistory")}
                  </button>
                </>
              )}
            </div>
          </>
        </DialogView>
      )}
    </>
  );
}
